/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { Image, Flex, Cell, Column, Row, TableView, TableBody, TableHeader } from '@adobe/react-spectrum';

import { formatPrices } from '../../utils/priceFormatting';

const messages = defineMessages({
    columnImage: {
        id: 'column.image',
        defaultMessage: 'Image'
    },
    columnName: {
        id: 'column.name',
        defaultMessage: 'Name'
    },
    columnSku: {
        id: 'column.sku',
        defaultMessage: 'SKU'
    },
    columnPrice: {
        id: 'column.price',
        defaultMessage: 'Price'
    },
    columnAttributes: {
        id: 'column.attributes',
        defaultMessage: 'Attributes'
    },
    labelTableOfProductVariants: {
        id: 'label.tableOfProductVariants',
        defaultMessage: 'Table of product variants'
    }
});

/**
 * Displays a table of product variants. To be used with products of type ConfigurableProduct.
 */
const VariantTable = props => {
    const { loading, items } = props;
    const { formatMessage, formatNumber } = useIntl();

    let rows = [];
    if (!loading) {
        rows = items.map(item => {
            const result = {
                name: item.product.name,
                sku: item.product.sku,
                formattedPrice: formatPrices(item.product.price_range, formatNumber).final_price,
                attributes: item.attributes.map(attribute => attribute.label).join(', ')
            };
            if (item.product.thumbnail && item.product.thumbnail.url) {
                result.image = item.product.thumbnail.url.replace('http://', '//');
            }
            return result;
        });
    }

    return (
        <Flex width="100%">
            <TableView
                minHeight="size-3000"
                flex
                isQuiet
                overflowMode="wrap"
                aria-label={formatMessage(messages.labelTableOfProductVariants)}
            >
                <TableHeader>
                    <Column key="image" width={100} hideHeader align="center">
                        {formatMessage(messages.columnImage)}
                    </Column>
                    <Column isRowHeader key="name">
                        {formatMessage(messages.columnName)}
                    </Column>
                    <Column key="sku">{formatMessage(messages.columnSku)}</Column>
                    <Column key="formattedPrice">{formatMessage(messages.columnPrice)}</Column>
                    <Column key="attributes">{formatMessage(messages.columnAttributes)}</Column>
                </TableHeader>
                <TableBody items={rows} loadingState={loading ? 'loading' : 'idle'}>
                    {item => (
                        <Row key={item.sku}>
                            {columnKey => {
                                if (columnKey === 'image') {
                                    return (
                                        <Cell>
                                            {item.image ? (
                                                <Flex justifyContent="center" height="size-1250" width="size-1250">
                                                    <Image objectFit="contain" src={item.image} alt={item.name} />
                                                </Flex>
                                            ) : null}
                                        </Cell>
                                    );
                                }
                                return <Cell>{item[columnKey]}</Cell>;
                            }}
                        </Row>
                    )}
                </TableBody>
            </TableView>
        </Flex>
    );
};

VariantTable.propTypes = {
    loading: PropTypes.bool,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            attributes: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string
                })
            ),
            product: PropTypes.shape({
                name: PropTypes.string.isRequired,
                sku: PropTypes.string.isRequired,
                thumbnail: PropTypes.shape({
                    url: PropTypes.string
                }),
                price_range: PropTypes.shape({
                    minimum_price: PropTypes.shape({
                        final_price: PropTypes.shape({
                            currency: PropTypes.string,
                            value: PropTypes.number
                        })
                    })
                })
            })
        })
    )
};

export default VariantTable;
